import React, { useRef } from 'react';
import { cn } from '@/utils/cn';

export interface TabItem {
  id: string;
  label: React.ReactNode;
  /** Optional count shown after the label. */
  count?: number;
  disabled?: boolean;
}

export interface TabsProps {
  tabs: TabItem[];
  value: string;
  onChange: (id: string) => void;
  /** `underline` for page and card sections, `segmented` for compact view switches. */
  variant?: 'underline' | 'segmented';
  /** Accessible name for the tab list. */
  label?: string;
  className?: string;
}

/**
 * The one tab bar. Renders the tablist only; the caller renders the active panel. Left/Right/Home/End move
 * between tabs.
 */
export const Tabs: React.FC<TabsProps> = ({ tabs, value, onChange, variant = 'underline', label, className }) => {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  const onKeyDown = (e: React.KeyboardEvent) => {
    const enabled = tabs.filter((t) => !t.disabled);
    const i = enabled.findIndex((t) => t.id === value);
    let next: TabItem | undefined;
    if (e.key === 'ArrowRight') next = enabled[(i + 1) % enabled.length];
    else if (e.key === 'ArrowLeft') next = enabled[(i - 1 + enabled.length) % enabled.length];
    else if (e.key === 'Home') next = enabled[0];
    else if (e.key === 'End') next = enabled[enabled.length - 1];
    if (!next) return;
    e.preventDefault();
    onChange(next.id);
    refs.current[next.id]?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label={label}
      onKeyDown={onKeyDown}
      className={cn(
        variant === 'underline'
          ? 'flex items-center gap-1 overflow-x-auto no-scrollbar border-b border-ui-border'
          : 'inline-flex items-center gap-1 rounded-full bg-ui-subtle p-1',
        className,
      )}
    >
      {tabs.map((t) => {
        const selected = t.id === value;
        return (
          <button
            key={t.id}
            ref={(el) => { refs.current[t.id] = el; }}
            role="tab"
            type="button"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            disabled={t.disabled}
            onClick={() => onChange(t.id)}
            className={cn(
              'shrink-0 whitespace-nowrap text-xs font-medium touch-manipulation transition-colors duration-150 focus-ring',
              'disabled:pointer-events-none disabled:opacity-50',
              variant === 'underline'
                ? cn(
                    '-mb-px border-b-2 px-3 py-2',
                    selected
                      ? 'border-accent text-accent-text'
                      : 'border-transparent text-ui-text2 hover:text-ui-text',
                  )
                : cn(
                    'rounded-full px-3 py-1',
                    selected ? 'bg-ui-card text-ui-text shadow-card' : 'text-ui-text2 hover:text-ui-text',
                  ),
            )}
          >
            {t.label}
            {t.count != null && <span className="ml-1.5 text-ui-text3">{t.count}</span>}
          </button>
        );
      })}
    </div>
  );
};

export default Tabs;
